import type { TeamSettings } from "@/lib/types";

/**
 * Pure helpers behind Coach > Settings' save flow.
 *
 * The settings screen exposes only manual coaching defaults. Old
 * `ai.*` settings may still be present in the API response while the
 * replacement feature is being built, so both the edit buffer and
 * the PATCH diff are restricted to these visible keys.
 *
 * `diffTeamSettings` returns only the keys whose value changed
 * between the server payload and the local edit buffer — the
 * PATCH body should carry just those (the backend applies a
 * partial update; sending unchanged keys is wasteful and widens
 * the validation surface). `isTeamSettingsDirty` is the boolean
 * the Save button's enabled state keys off.
 *
 * Editable values are schema-backed strings, so JSON equality is exact.
 */

const eq = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

export const DEFAULTS_KEYS = [
  "notes.default_visibility",
  "summaries.default_visibility",
  "goals.default_visibility",
] as const;

type EditableTeamSettingKey = (typeof DEFAULTS_KEYS)[number];

/** H15 UI-13: fixed Feedback entry behavior replaces the Note default control.
 * Keep the legacy registry compatibility until the coordinated #851 cutover. */
export const CANONICAL_DEFAULTS_KEYS = [
  "summaries.default_visibility",
  "goals.default_visibility",
] as const;

/** Project an API response into the values this page can edit. */
export function projectEditableTeamSettings(
  settings: TeamSettings,
  keys: readonly EditableTeamSettingKey[] = DEFAULTS_KEYS,
): TeamSettings {
  const projected: TeamSettings = {};
  for (const key of keys) {
    const value = settings[key];
    if (value !== undefined) {
      (projected as Record<string, unknown>)[key] = value;
    }
  }
  return projected;
}

/** The subset of `draft` whose values differ from `server`. */
export function diffTeamSettings(
  server: TeamSettings,
  draft: TeamSettings,
  keys: readonly EditableTeamSettingKey[] = DEFAULTS_KEYS,
): TeamSettings {
  const changed: TeamSettings = {};
  for (const key of keys) {
    // Missing draft keys are not edits. This also avoids turning a
    // partially loaded/older payload into a PATCH that clears defaults.
    if (!(key in draft) || draft[key] === undefined) continue;
    if (!eq(draft[key], server[key])) {
      (changed as Record<string, unknown>)[key] = draft[key];
    }
  }
  return changed;
}

/** True when the draft differs from the server payload in any key. */
export function isTeamSettingsDirty(
  server: TeamSettings,
  draft: TeamSettings,
  keys: readonly EditableTeamSettingKey[] = DEFAULTS_KEYS,
): boolean {
  return Object.keys(diffTeamSettings(server, draft, keys)).length > 0;
}
